/**
 * Option is an abstract class that represents an optional value:
 * every `Option` is either [[`Some`]] and contains a value, or [[`None`]]
 * and does not.
 *
 * @typeParam T The type of the value contained in the `Option`.
 */
class Option {}

/**
 * A `Some` is an [[`Option`]] that contains a value.
 */

class Some extends Option {
  constructor(value) {
    super();
    this.value = value;
  }

  isSome() {
    return true;
  }

  isNone() {
    return false;
  }

  expect(message) {
    return this.value;
  }

  unwrap() {
    return this.value;
  }

  unwrapOr(defaultValue) {
    return this.value;
  }

  unwrapOrElse(fn) {
    return this.value;
  }

  map(f) {
    return new Some(f(this.value));
  }

  mapOr(defaultValue, f) {
    return f(this.value);
  }

  mapOrElse(defaultFunc, f) {
    return f(this.value);
  }

  and(optB) {
    return optB;
  }

  andThen(fn) {
    return fn(this.value);
  }

  filter(predicate) {
    if (predicate(this.value)) return new Some(this.value);
    return new None();
  }

  or(optB) {
    return new Some(this.value);
  }

  orElse(fn) {
    return new Some(this.value);
  }

  xor(optB) {
    if (optB.isNone()) return new Some(this.value);
    return new None();
  }

  zip(other) {
    if (other.isSome()) return new Some([this.value, other.value]);
    return new None();
  }

  zipWith(other, fn) {
    if (other.isSome()) return new Some(fn(this.value, other.value));
    return new None();
  }

}

/**
 * A `None` is an [[`Option`]] that does not contain a value.
 */

class None extends Option {
  isSome() {
    return false;
  }

  isNone() {
    return true;
  }

  expect(message) {
    throw new Error(message);
  }

  unwrap() {
    throw new Error(`called \`Option.unwrap()\` on a \`None\` value`);
  }

  unwrapOr(defaultValue) {
    return defaultValue;
  }

  unwrapOrElse(fn) {
    return fn();
  }

  map(f) {
    return new None();
  }

  mapOr(defaultValue, f) {
    return defaultValue;
  }

  mapOrElse(defaultFunc, f) {
    return defaultFunc();
  }

  and(optB) {
    return new None();
  }

  andThen(fn) {
    return new None();
  }

  filter(predicate) {
    return new None();
  }

  or(optB) {
    return optB;
  }

  orElse(fn) {
    return fn();
  }

  xor(optB) {
    if (optB.isSome()) return new Some(optB.unwrap());
    return new None();
  }

  zip(other) {
    return new None();
  }

  zipWith(other, fn) {
    return new None();
  }

}

export { None, Option, Some };
